import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtOrEmpty, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { RAB_MANAGE, RAB_MONEY } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, PlusCircle, Boxes, ArrowRight, Search, AlertTriangle } from "lucide-react";

export default function Rab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && RAB_MANAGE.includes(user.role);
  const showMoney = user && RAB_MONEY.includes(user.role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");

  const load = () => { setLoading(true); api.get("/rab", { params: q ? { q } : {} }).then(({ data }) => setList(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = async () => { const { data } = await api.get("/projects"); setProjects(data); setOpen(true); };
  const create = async () => { if (!pid) return; try { const { data } = await api.post("/rab", { project_uuid: pid, items: [] }); toast.success(`RAB ${data.rab_no} dibuat`); navigate(`/rab/${data.id}`); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4" data-testid="rab-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">RAB Bahan</h1><p className="text-sm text-muted-foreground">{list.length} RAB · per proyek & kendaraan</p></div>
        {canManage && <Button onClick={openCreate} data-testid="new-rab-btn" className="bg-primary hover:bg-red-700"><PlusCircle size={16} className="mr-1.5" /> Buat RAB</Button>}
      </div>
      <Card className="p-3"><form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no. RAB, proyek, customer, no. polisi..." className="pl-9" data-testid="rab-search" />
      </form></Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><Boxes className="mx-auto mb-2 opacity-40" /> Belum ada RAB.</Card> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((r) => {
              const over = showMoney && (r.total_actual_sum || 0) > (r.total_plan_sum || 0) && (r.total_plan_sum || 0) > 0;
              return (
                <Card key={r.id} data-testid={`rab-card-${r.rab_no}`} className="p-4 space-y-1.5 cursor-pointer hover:shadow-md" onClick={() => navigate(`/rab/${r.id}`)}>
                  <div className="flex items-center justify-between"><span className="font-mono-k3 text-xs font-semibold text-primary">{r.rab_no}</span>
                    {over && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle size={11} /> Over Budget</span>}</div>
                  <div className="font-semibold">{r.plate} · {r.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{r.project_id} · {r.work_type || "-"} · {r.items?.length || 0} item</div>
                  <div className="text-[11px] text-muted-foreground" data-testid={`rab-meta-${r.rab_no}`}>Disusun {fmtDate(r.created_at)} · Status: {r.status || "-"}</div>
                  {showMoney && <div className="text-xs">Total RAB: <b data-testid={`rab-total-${r.rab_no}`}>{r.items?.length ? fmtOrEmpty(r.total_plan_sum) : "BELUM TERISI"}</b></div>}
                  <div className="flex justify-end"><ArrowRight size={14} className="text-muted-foreground" /></div>
                </Card>
              );
            })}
          </div>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Buat RAB Baru</DialogTitle></DialogHeader>
          <Select value={pid} onValueChange={setPid}><SelectTrigger data-testid="rab-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_id} · {p.plate} · {p.customer_name}</SelectItem>)}</SelectContent></Select>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button><Button onClick={create} data-testid="rab-create-submit" className="bg-primary hover:bg-red-700">Buat</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
